import { useState, useEffect } from 'react';
import { getForestStands, createForestStand, updateForestStand, deleteForestStand, getStandSpecies, updateStandSpecies } from '../services/api';
import { calculateAreaHectares } from '../utils/polygonCalculations';

export const useStands = (farmId, setMessage, setMode) => {
    const [forestStands, setForestStands] = useState({ type: 'FeatureCollection', features: [] });
    const [selectedStand, setSelectedStand] = useState(null);
    const [standDetails, setStandDetails] = useState({
        stand_number: 1,
        area_hectares: 0,
        age: 0,
        site_index: 'G24',
        volume_m3sk_ha: 0,
        goal_class: 'PG',
        stems_per_ha: 0
    });
    const [standSpecies, setStandSpecies] = useState([]);

    const loadStands = async (id) => {
        if (!id) {
            setForestStands({ type: 'FeatureCollection', features: [] });
            return;
        }
        try {
            const stands = await getForestStands(id);
            
            // Check for missing area data and update if necessary
            let needsReload = false;
            for (const stand of stands) {
                const areaHectares = parseFloat(stand.area_hectares);
                if (!areaHectares || areaHectares === 0 || isNaN(areaHectares)) {
                    if (stand.geojson) {
                        const calculatedArea = calculateAreaHectares(stand.geojson);
                        if (calculatedArea > 0) {
                            console.log(`Auto-calculating area for Stand ${stand.id}: ${calculatedArea} ha`);
                            await updateForestStand(stand.id, { 
                                ...stand, 
                                area_hectares: calculatedArea 
                            });
                            needsReload = true;
                        }
                    }
                }
            }

            // If we updated any stands, reload the list to get fresh data
            if (needsReload) {
                const updatedStands = await getForestStands(id);
                setForestStands({
                    type: 'FeatureCollection',
                    features: updatedStands.map(s => ({
                        type: 'Feature',
                        id: s.id,
                        geometry: s.geojson,
                        properties: { ...s, isStand: 'true' }
                    }))
                });
            } else {
                setForestStands({
                    type: 'FeatureCollection',
                    features: stands.map(s => ({
                        type: 'Feature',
                        id: s.id,
                        geometry: s.geojson,
                        properties: { ...s, isStand: 'true' }
                    }))
                });
            }
        } catch (err) {
            console.error("Could not load stands", err);
        }
    };

    const handleForestStandSelect = async (feature) => {
        if (!feature) {
            setSelectedStand(null);
            setStandSpecies([]);
            return;
        }
        const stand = feature.properties;
        setSelectedStand(feature);
        setStandDetails({
            stand_number: stand.stand_number,
            area_hectares: stand.area_hectares || 0,
            age: stand.age || 0,
            site_index: stand.site_index || 'G24',
            volume_m3sk_ha: stand.volume_m3sk_ha || 0,
            goal_class: stand.goal_class || 'PG',
            stems_per_ha: stand.stems_per_ha || 0
        });
        
        try {
            const species = await getStandSpecies(feature.id);
            setStandSpecies(species);
        } catch (err) {
            console.error("Error loading stand species", err);
            setStandSpecies([]);
        }

        setMode('view');
    };

    const handleForestStandCreate = async (feature, areaHectares, currentArea) => {
        if (!farmId) {
            setMessage({ text: 'Please save a farm first', type: 'warning', open: true });
            return;
        }

        try {
            const standData = {
                geom_geojson: feature.geometry,
                area_hectares: areaHectares || currentArea,
                age: 0,
                site_index: 'G24',
                volume_m3sk_ha: 0,
                goal_class: 'PG',
                measurement_date: new Date().toISOString().split('T')[0]
            };

            await createForestStand(farmId, standData);
            setMessage({ text: 'Forest stand saved!', type: 'success', open: true });
            
            await loadStands(farmId);
            setMode('view');
            return true;
        } catch (err) {
            console.error("Error saving forest stand:", err);
            setMessage({ text: 'Failed to save forest stand', type: 'error', open: true });
            return false;
        }
    };

    const handleUpdateStand = async () => {
        if (!selectedStand || !farmId) return;
        try {
            await updateForestStand(selectedStand.id, standDetails);
            await updateStandSpecies(selectedStand.id, standSpecies);
            setMessage({ text: 'Forest stand updated!', type: 'success', open: true });
            loadStands(farmId);
        } catch (err) {
            console.error("Error updating stand:", err);
            setMessage({ text: 'Failed to update stand', type: 'error', open: true });
        }
    };

    const handleDeleteStand = async () => {
        if (!selectedStand || !farmId) return;
        if (!window.confirm('Are you sure you want to delete this forest stand?')) return;
        try {
            await deleteForestStand(selectedStand.id);
            setMessage({ text: 'Forest stand deleted!', type: 'success', open: true });
            loadStands(farmId);
            setSelectedStand(null);
        } catch (err) {
            console.error("Error deleting stand:", err);
            setMessage({ text: 'Failed to delete stand', type: 'error', open: true });
        }
    };

    return {
        forestStands,
        selectedStand,
        setSelectedStand,
        standDetails,
        setStandDetails,
        standSpecies,
        setStandSpecies,
        loadStands,
        handleForestStandSelect,
        handleForestStandCreate,
        handleUpdateStand,
        handleDeleteStand
    };
};
